(() => {
  const escape = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
  const discounted = (cents) => Math.round(cents * 90 / 100);
  const normalize = (product) => ({
    ...product,
    id: String(product.id),
    images: (product.images || []).map((image) => typeof image === 'string' ? image : image.src),
    options: (product.options || []).map((option, index) => typeof option === 'string' ? { name: option, values: [...new Set(product.variants.map((variant) => variant.options[index]))] } : option),
    variants: (product.variants || []).map((variant) => ({ ...variant, id: String(variant.id), options: variant.options || [variant.option1, variant.option2, variant.option3].filter(Boolean) })),
  });

  class SpotitBundle extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      const source = this.querySelector('script[type="application/json"]');
      if (!this.config && !source?.textContent?.trim()) {
        this.initObserver = new MutationObserver(() => {
          if (this.querySelector('script[type="application/json"]')?.textContent?.trim()) { this.initObserver.disconnect(); this.connectedCallback(); }
        });
        this.initObserver.observe(this, { childList: true, subtree: true, characterData: true });
        return;
      }
      try { if (!this.config) this.config = JSON.parse(source.textContent); } catch { this.textContent = 'The bundle offer could not be loaded.'; return; }
      if (!this.config.primary) return;
      this.initialized = true;
      this.primary = normalize(this.config.primary);
      this.products = (this.config.products || []).map(normalize).filter((product) => product.variants.some((variant) => variant.available));
      this.primaryVariant = this.primary.variants.find((variant) => variant.id === String(this.config.selectedVariantId)) || this.primary.variants.find((variant) => variant.available) || this.primary.variants[0];
      this.mode = 'standard';
      this.second = null;
      this.modalVariants = Object.fromEntries(this.products.map((product) => [product.id, (product.variants.find((variant) => variant.available) || product.variants[0]).id]));
      this.baseCurrency = this.config.currency || 'USD';
      this.currency = this.baseCurrency;
      this.native = Boolean(this.config.nativeCart);
      this.currencyHandler = (event) => this.updateCurrency(event.detail?.currency);
      this.storageHandler = (event) => { if (event.key === 'selectedCurrency') this.updateCurrency(event.newValue); };
      document.addEventListener('spotit:currency-changed', this.currencyHandler);
      window.addEventListener('storage', this.storageHandler);
      this.updateCurrency();
      this.clickHandler = (event) => this.onClick(event);
      this.changeHandler = (event) => this.onChange(event);
      this.keyHandler = (event) => {
        if (event.target.closest('select, button, input, a')) return;
        const target = event.target.closest('[data-mode]');
        if (target && (event.key === ' ' || event.key === 'Enter')) { event.preventDefault(); this.setMode(target.dataset.mode); }
        if (target && ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) { event.preventDefault(); this.setMode(this.mode === 'standard' ? 'bundle' : 'standard'); this.querySelector(`[data-mode="${this.mode}"]`).focus(); }
      };
      this.addEventListener('click', this.clickHandler);
      this.addEventListener('change', this.changeHandler);
      this.addEventListener('keydown', this.keyHandler);
      this.render();
      if (this.native) this.bindNativeForm();
      this.emit();
    }

    updateCurrency(currency) {
      let savedCurrency = null;
      try { savedCurrency = localStorage.getItem('selectedCurrency'); } catch (error) { /* Storage can be blocked in theme previews. */ }
      this.currency = currency || window.SpotItCurrency?.getCurrency?.() || savedCurrency || this.baseCurrency;
      this.querySelectorAll('[data-sb-money]').forEach((node) => {
        node.textContent = this.formatMoney(Number(node.dataset.sbMoney));
      });
    }
    formatMoney(cents) {
      const baseAmount = cents / 100;
      const converted = this.currency === this.baseCurrency ? baseAmount : window.SpotItCurrency?.convertAmount?.(baseAmount, this.baseCurrency, this.currency);
      const hasConversion = typeof converted === 'number' && Number.isFinite(converted);
      const currency = hasConversion ? this.currency : this.baseCurrency;
      const amount = hasConversion ? converted : baseAmount;
      const rounded = Math.floor(amount);
      return new Intl.NumberFormat('en-US', { style: 'currency', currency, currencyDisplay: currency === 'USD' ? 'symbol' : 'code', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(rounded);
    }
    money(cents) { return `<span class="sb-money" data-sb-money="${Number(cents)}">${escape(this.formatMoney(cents))}</span>`; }
    totals() {
      const original = this.primaryVariant.price + (this.second?.variant.price ?? this.primaryVariant.price);
      return { original, total: discounted(original), confirmed: Boolean(this.second) };
    }
    emit() {
      const totals = this.totals();
      this.dispatchEvent(new CustomEvent('bundle-change', { bubbles: true, detail: { mode: this.mode, primaryVariant: this.primaryVariant, second: this.second, total: this.mode === 'bundle' ? totals.total : this.primaryVariant.price, original: this.mode === 'bundle' ? totals.original : this.primaryVariant.price, ready: this.primaryVariant.available && (this.mode === 'standard' || Boolean(this.second?.variant.available)) } }));
    }
    setMode(mode) { this.mode = mode; this.render(); this.syncNativePayment(); this.emit(); }
    setPrimaryVariant(id) {
      const variant = this.primary.variants.find((candidate) => candidate.id === String(id));
      if (variant && variant.id !== this.primaryVariant.id) { this.primaryVariant = variant; this.render(); this.emit(); }
    }
    selectors(product, variant, owner) {
      return `<div class="sb-selectors">${product.options.map((option, index) => `<label class="sb-option"><span class="sb-sr-only">${escape(option.name)}</span><select aria-label="${escape(owner === 'primary' ? 'First item' : 'Second item')} ${escape(option.name)}" data-owner="${escape(owner)}" data-option="${index}" data-product="${escape(product.id)}">${option.values.map((value) => {
        const options = [...variant.options]; options[index] = value;
        const available = product.variants.some((candidate) => candidate.available && candidate.options.every((selected, optionIndex) => selected === options[optionIndex]));
        return `<option value="${escape(value)}" ${variant.options[index] === value ? 'selected' : ''} ${available ? '' : 'disabled'}>${escape(value)}${available ? '' : ' — sold out'}</option>`;
      }).join('')}</select></label>`).join('')}</div>`;
    }
    primaryDetails() { return `<div class="sb-primary"><p class="sb-product-title">${escape(this.primary.title)}</p>${this.selectors(this.primary, this.primaryVariant, 'primary')}</div>`; }
    secondDetails() {
      if (!this.second) return '';
      const { product, variant } = this.second;
      return `<div class="sb-chosen"><div class="sb-chosen-header"><span class="sb-eyebrow">YOUR SECOND ITEM</span><button type="button" class="sb-remove" data-action="remove" aria-label="Remove second product">×</button></div><div class="sb-chosen-product"><img src="${escape(product.images[0])}" alt="${escape(product.title)}"><div><p class="sb-product-title">${escape(product.title)}</p><p class="sb-chosen-price">${this.money(discounted(variant.price))}<s>${this.money(variant.price)}</s></p></div></div>${this.selectors(product, variant, 'second')}</div>`;
    }
    render() {
      if (this.dialog) this.closePicker();
      const totals = this.totals();
      const standard = this.mode === 'standard';
      const bundleDetails = standard ? '' : `${this.primaryDetails()}${this.secondDetails()}<button type="button" class="sb-explore" data-action="explore">${this.second ? 'Change Second Product' : 'Choose Second Product'}</button>`;
      const footer = standard ? '' : this.second ? `<p class="sb-savings"><span>Two pieces. One better price.</span><strong>You save ${this.money(totals.original - totals.total)}</strong></p>` : `<p class="sb-hint">Choose a second product to confirm your bundle total.</p>`;
      const cartButton = this.native && !standard && this.second ? `<button type="button" class="sb-add" data-action="add" ${!this.primaryVariant.available || !this.second.variant.available ? 'disabled' : ''}>Add bundle to cart — ${this.money(totals.total)}</button>` : '';
      this.innerHTML = `<div class="sb-offer"><h2 class="sb-heading"><span>Exclusive Bundle Offer</span></h2><div class="sb-cards" role="radiogroup" aria-label="Purchase option">
        <div class="sb-card ${standard ? 'sb-active' : ''}" data-mode="standard" role="radio" aria-checked="${standard}" tabindex="${standard ? '0' : '-1'}"><span class="sb-radio" aria-hidden="true"></span><h3>Standard Purchase</h3><p class="sb-subtitle">Standard price</p><p class="sb-price">${this.money(this.primaryVariant.price)}</p>${standard ? this.primaryDetails() : ''}</div>
        <div class="sb-card ${!standard ? 'sb-active' : ''}" data-mode="bundle" role="radio" aria-checked="${!standard}" tabindex="${!standard ? '0' : '-1'}"><span class="sb-radio" aria-hidden="true"></span><h3>Exclusive Bundle</h3><p class="sb-subtitle">Shop 2 & save 10%</p><p class="sb-price">${this.money(totals.total)}</p><s class="sb-original">${this.money(totals.original)}</s>${bundleDetails}</div>
      </div>${footer}${cartButton}<p class="sb-error" role="alert"></p></div>`;
    }
    onClick(event) {
      const action = event.target.closest('[data-action]');
      if (action) {
        const name = action.dataset.action;
        if (name === 'explore') this.openPicker();
        if (name === 'close') this.closePicker();
        if (name === 'choose') this.choose(action.dataset.product);
        if (name === 'remove') { this.second = null; this.render(); this.emit(); }
        if (name === 'add') this.addNative();
        return;
      }
      if (event.target.closest('select, button, a')) return;
      const productRow = event.target.closest('.sb-row[data-product]');
      if (productRow) { this.choose(productRow.dataset.product); return; }
      const card = event.target.closest('[data-mode]');
      if (card) this.setMode(card.dataset.mode);
    }
    onChange(event) {
      const select = event.target.closest('select[data-owner]');
      if (!select) return;
      const owner = select.dataset.owner;
      const product = owner === 'primary' ? this.primary : owner === 'second' ? this.second.product : this.products.find((candidate) => candidate.id === select.dataset.product);
      const current = owner === 'primary' ? this.primaryVariant : owner === 'second' ? this.second.variant : product.variants.find((variant) => variant.id === this.modalVariants[product.id]);
      const options = [...current.options];
      options[Number(select.dataset.option)] = select.value;
      const variant = product.variants.find((candidate) => candidate.options.every((value, index) => value === options[index]));
      if (!variant || (!variant.available && owner !== 'picker')) { select.value = current.options[Number(select.dataset.option)]; this.showError('That combination is sold out or unavailable. Please choose another option.'); return; }
      if (owner === 'primary') { this.primaryVariant = variant; this.syncNativeVariant(); this.render(); this.emit(); }
      else if (owner === 'second') { this.second.variant = variant; this.render(); this.emit(); }
      else { this.modalVariants[product.id] = variant.id; this.renderRows(); }
    }
    openPicker() {
      if (this.dialog) return;
      if (this.mode !== 'bundle') this.setMode('bundle');
      const dialog = document.createElement('dialog');
      dialog.className = 'sb-dialog';
      dialog.setAttribute('aria-labelledby', 'sb-picker-title');
      dialog.innerHTML = `<div class="sb-dialog-top"><div><span class="sb-eyebrow">BUILD YOUR BUNDLE</span><h2 id="sb-picker-title">Choose product</h2><p>Add a second piece. Save 10% on both.</p></div><button type="button" class="sb-close" data-action="close" aria-label="Close product picker">×</button></div><div class="sb-search"><input type="search" placeholder="Search products…" aria-label="Search second products"></div><div class="sb-rows"></div><div class="sb-dialog-footer">10% off both items · Mix brands, sizes & colors</div>`;
      this.append(dialog);
      this.dialog = dialog;
      this.query = '';
      this.renderRows();
      dialog.querySelector('input').addEventListener('input', (event) => { this.query = event.target.value; this.renderRows(); });
      dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
      this.previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      dialog.addEventListener('close', () => { this.cleanupPicker(dialog); }, { once: true });
      dialog.showModal();
      dialog.querySelector('.sb-close').focus();
    }
    cleanupPicker(dialog) {
      if (this.dialog !== dialog) return;
      document.body.style.overflow = this.previousOverflow || '';
      dialog.remove(); this.dialog = null;
      this.querySelector('[data-action="explore"]')?.focus();
    }
    closePicker() {
      const dialog = this.dialog;
      if (!dialog) return;
      if (dialog.open) dialog.close();
      this.cleanupPicker(dialog);
    }
    renderRows() {
      const filtered = this.products.filter((product) => product.title.toLowerCase().includes((this.query || '').toLowerCase()));
      this.dialog.querySelector('.sb-rows').innerHTML = filtered.length ? filtered.map((product) => {
        const variant = product.variants.find((candidate) => candidate.id === this.modalVariants[product.id]);
        return `<article class="sb-row" data-product="${escape(product.id)}"><img src="${escape(product.images[0])}" alt="${escape(product.title)}" loading="lazy"><div class="sb-row-info"><h3>${escape(product.title)}</h3><p class="sb-row-price"><strong>${this.money(discounted(variant.price))}</strong><s>${this.money(variant.price)}</s><span>−10%</span></p><p class="sb-row-label">${product.options.map((option) => escape(option.name)).join(', ')}</p>${this.selectors(product, variant, 'picker')}<p class="sb-stock">${variant.available ? 'Available' : 'This variant is sold out'}</p></div><button type="button" class="sb-choose" data-action="choose" data-product="${escape(product.id)}" ${variant.available ? '' : 'disabled'}>${variant.available ? 'Choose' : 'Sold out'}</button></article>`;
      }).join('') : `<div class="sb-empty">No products found. Try another search.</div>`;
    }
    choose(id) {
      const product = this.products.find((candidate) => candidate.id === id);
      const variant = product?.variants.find((candidate) => candidate.id === this.modalVariants[id]);
      if (!variant?.available) return;
      this.closePicker();
      this.mode = 'bundle';
      this.second = { product, variant };
      this.render();
      this.syncNativePayment();
      this.emit();
      this.querySelector('[data-action="explore"]')?.focus();
    }
    showError(message) { const node = this.querySelector('.sb-error'); if (node) node.textContent = message; }
    bindNativeForm() {
      const section = this.closest('.shopify-section') || document;
      this.nativeSection = section;
      this.form = section.querySelector('form[id^="AddToCartForm-"]') || section.querySelector('form[action*="/cart/add"]');
      if (!this.form) { this.showError('The product form could not be found. The separate bundle button is still available.'); return; }
      this.readFormVariant = () => this.setPrimaryVariant(this.form.querySelector('[name="id"]')?.value);
      this.formChangeHandler = () => setTimeout(this.readFormVariant, 0);
      this.form.addEventListener('change', this.formChangeHandler);
      this.nativeSubmit = (event) => {
        if (this.mode !== 'bundle') return;
        event.preventDefault(); event.stopImmediatePropagation();
        this.readFormVariant(); this.addNative();
      };
      this.form.addEventListener('submit', this.nativeSubmit, true);
      this.nativeClick = (event) => {
        if (this.mode !== 'bundle' || !event.target.closest('.sb-add, .js-express-checkout, buy-now, .product-form__cart-submit')) return;
        event.preventDefault(); event.stopImmediatePropagation();
        this.readFormVariant(); this.addNative();
      };
      section.addEventListener('click', this.nativeClick, true);
      this.readFormVariant();
    }
    syncNativeVariant() {
      if (!this.form) return;
      const input = this.form.querySelector('[name="id"]');
      const selectedControls = [];
      this.primary.options.forEach((option, index) => {
        this.form.querySelectorAll('input[type="radio"]').forEach((radio) => { if (radio.value === this.primaryVariant.options[index]) { radio.checked = true; selectedControls.push(radio); } });
        this.form.querySelectorAll(`select[name="options[${option.name}]"]`).forEach((select) => { select.value = this.primaryVariant.options[index]; selectedControls.push(select); });
      });
      if (input) input.value = this.primaryVariant.id;
      selectedControls.forEach((control) => control.dispatchEvent(new Event('change', { bubbles: true })));
      if (input) input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    syncNativePayment() {
      if (!this.form) return;
      this.form.classList.toggle('sb-bundle-mode', this.mode === 'bundle');
      this.nativeSection?.classList?.toggle('sb-section-bundle-mode', this.mode === 'bundle');
    }
    async cartRequest(path, data) {
      const root = window.Shopify?.routes?.root || '/';
      // Use the same Ajax transport as the theme, including existing app hooks.
      if (window.jQuery?.ajax) {
        return new Promise((resolve, reject) => window.jQuery.ajax({
          url: `${root}${path}`, type: 'POST', contentType: 'application/json', dataType: 'json', data: JSON.stringify(data),
          success: resolve,
          error: (xhr) => reject(new Error(xhr.responseJSON?.description || xhr.responseJSON?.message || 'Unable to update your cart. Please try again.')),
        }));
      }
      const response = await fetch(`${root}${path}`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(data) });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || 'Unable to update your cart. Please try again.');
      return result;
    }
    refreshNativeDrawer(cart) {
      const drawer = window.theme?.miniCart;
      const ids = [this.primaryVariant.id, this.second.variant.id].map(String);
      window.SpotItDrawerPriority = ids;
      if (drawer?.showBundle) drawer.showBundle(ids, cart);
      else { drawer?.refreshCart?.(); drawer?.openDrawer?.(); }
    }
    async addNative() {
      if (this.busy) return;
      if (!this.second) { this.openPicker(); return; }
      if (!this.primaryVariant.available || !this.second.variant.available) { this.showError('Please select available variants for both items.'); return; }
      const button = this.querySelector('.sb-add');
      this.busy = true;
      this.showError('');
      if (button) { button.disabled = true; button.textContent = 'Adding your bundle…'; }
      try {
        if (!window.theme?.miniCart?.refreshCart || !window.theme?.miniCart?.openDrawer) throw new Error('The cart is still loading. Please try again in a moment.');
        if (!this.pendingCartAddition) {
          await this.cartRequest('cart/add.js', { items: [this.primaryVariant, this.second.variant].map((variant) => ({ id: variant.id, quantity: 1, properties: { '_SpotIt Bundle': '10% pair' } })) });
          this.pendingCartAddition = true;
        }
        const code = this.config.discountCode || 'Bundle10';
        let cart;
        // Apply through the installed discount UI once, then verify Shopify's result.
        // Applying through Ajax and then the app caused redundant recalculations.
        if (typeof window.DiscountHero?.discount?.apply === 'function') {
          try {
            let saved = '';
            try { saved = localStorage.getItem('sc:discount.code') || ''; } catch { /* Storage can be blocked. */ }
            if (saved.toLowerCase().includes('bundle15') && typeof window.DiscountHero.discount.remove === 'function') await window.DiscountHero.discount.remove('Bundle15');
            await window.DiscountHero.discount.apply(code);
            const root = window.Shopify?.routes?.root || '/';
            const response = await fetch(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
            if (response.ok) cart = await response.json();
          } catch { /* Use the native fallback only when the app cannot apply. */ }
        }
        const applied = (result) => Array.isArray(result?.discount_codes) && result.discount_codes.some((discount) => discount.code.toLowerCase() === code.toLowerCase() && discount.applicable);
        if (!applied(cart)) cart = await this.cartRequest('cart/update.js', { discount: code });
        if (!applied(cart)) throw new Error('Your items are in the cart, but Bundle10 could not be applied. Please review the discount in the cart.');
        this.pendingCartAddition = false;
        this.refreshNativeDrawer(cart);
      } catch (error) {
        if (this.pendingCartAddition) this.refreshNativeDrawer();
        this.showError(this.pendingCartAddition ? `${error.message} Your two items are already added; retrying will only reapply the discount.` : error.message || 'Unable to add your bundle. Please try again.');
      } finally {
        this.busy = false;
        const currentButton = this.querySelector('.sb-add');
        if (currentButton) { currentButton.disabled = !this.primaryVariant.available || !this.second?.variant.available; currentButton.innerHTML = this.pendingCartAddition ? 'Retry bundle discount' : `Add bundle to cart — ${this.money(this.totals().total)}`; }
      }
    }
    disconnectedCallback() {
      this.initObserver?.disconnect();
      document.removeEventListener('spotit:currency-changed', this.currencyHandler);
      window.removeEventListener('storage', this.storageHandler);
      if (this.form) { this.form.removeEventListener('change', this.formChangeHandler); this.form.removeEventListener('submit', this.nativeSubmit, true); this.form.classList.remove('sb-bundle-mode'); }
      if (this.nativeSection) { this.nativeSection.removeEventListener('click', this.nativeClick, true); this.nativeSection.classList?.remove('sb-section-bundle-mode'); }
      this.closePicker();
      this.removeEventListener('click', this.clickHandler);
      this.removeEventListener('change', this.changeHandler);
      this.removeEventListener('keydown', this.keyHandler);
      this.form = null; this.nativeSection = null; this.initialized = false;
    }
  }
  if (!customElements.get('spotit-bundle')) customElements.define('spotit-bundle', SpotitBundle);
})();
